import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Badge,
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader
} from '@mui/material';
import LogoText from 'atoms/LogoText';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import NotificationDialog from 'organisms/Notification/NotificationDialog/NotificationDialog';
import React, { useCallback, useEffect, useState } from 'react';
import { useStore } from 'stores/store';
import { IMenuItem, INavItem } from 'utils/globalTypes';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { filterNavItem } from 'utils/navigateUtils';
import { nullToEmptyStr } from 'utils/object';
import { sameParent, sameUrl } from 'utils/urlUtils';

import s from './Burger.module.scss';

interface IBurger {
  navItems: Array<INavItem>;
  transparent?: boolean;
  dark?: boolean;
  isSticky?: boolean;
  onAction?: (actionName: string, url?: string, newTab?: boolean) => void;
}

const Burger = ({ navItems, transparent, dark, isSticky, onAction }: IBurger) => {
  const store = useStore();
  const authDetails = useUserAuth();
  const router = useRouter();

  const [openSubMenu, setOpenSubMenu] = useState('');

  useEffect(() => {
    const parentRoute = `/${router?.pathname.split('/')[1]}`;
    const activeNavItem = navItems.find(({ url }) => url === parentRoute);
    setOpenSubMenu(activeNavItem?.name ?? '');
  }, [router, navItems]);

  const toggleSubMenu = useCallback(
    (subMenu: string, event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setOpenSubMenu(openSubMenu === subMenu ? '' : subMenu);
    },
    [openSubMenu]
  );

  const toggleDrawer = useCallback(
    (open?: boolean) => {
      store.UIStore.setBurgerActive(open);
    },
    [store.UIStore]
  );

  const isActiveItem = useCallback(
    (item?: INavItem) => {
      return !!(
        (!item?.newTab && sameUrl(router, item?.url)) ||
        (item?.otherUrls && item?.otherUrls?.some((url) => sameParent(router, url))) ||
        item?.menuItems?.filter((item) => !item.newTab && sameParent(router, item?.url)).length
      );
    },
    [router]
  );

  const isActiveSubItem = useCallback(
    (item?: IMenuItem) => {
      return !item?.newTab && sameUrl(router, item?.url);
    },
    [router]
  );

  const isOpen = store.UIStore.isBurgerActive;
  const count =
    store.NotificationStore.unreadDirectCount > 0
      ? store.NotificationStore.unreadDirectCount < 9
        ? store.NotificationStore.unreadDirectCount
        : '9+'
      : '';

  const getMenuItem = (index: number, item: INavItem) => {
    const open = openSubMenu === item.name;

    if (item.url == 'notifications') {
      return (
        <ListItemButton
          key={`${item.name}-${index}`}
          onClick={() => onAction && onAction(item.name, item.url, item.newTab)}
        >
          <ListItemText
            disableTypography={true}
            className={cn(
              s.Burger__menuItem,
              { isActive: isActiveItem(item) },
              nullToEmptyStr(item.className)
            )}
            primary={item.name}
          />
          <Badge
            badgeContent={count || 0}
            color="primary"
            invisible={!count}
            sx={{ marginRight: '11px' }}
          />
        </ListItemButton>
      );
    }

    if (isEmpty(item.menuItems)) {
      return (
        <ListItemButton
          key={`${item.name}-${index}`}
          onClick={() => onAction && onAction(item.name, item.url, item.newTab)}
        >
          <ListItemText
            disableTypography={true}
            className={cn(
              s.Burger__menuItem,
              { isActive: isActiveItem(item) },
              nullToEmptyStr(item.className)
            )}
            primary={item.name}
          />
        </ListItemButton>
      );
    } else {
      return (
        <div key={`${item.name}-${index}`}>
          <ListItemButton onClick={(event) => toggleSubMenu(item.name, event)}>
            <ListItemText
              disableTypography={true}
              className={cn(s.Burger__menuItem, nullToEmptyStr(item.className))}
              primary={item.name}
            />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item?.menuItems
                ?.filter((item) => filterNavItem(item, authDetails))
                ?.map((subItem, i) => (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    key={`${item.name}-${subItem.name}-${i}`}
                    onClick={() => onAction && onAction(subItem.name, subItem.url, subItem.newTab)}
                  >
                    <ListItemText
                      disableTypography={true}
                      className={cn(
                        s.Burger__menuItem,
                        s.Burger__subMenuItem,
                        { isActive: isActiveSubItem(subItem) },
                        nullToEmptyStr(subItem.className)
                      )}
                      primary={subItem.name}
                    />
                  </ListItemButton>
                ))}
            </List>
          </Collapse>
        </div>
      );
    }
  };

  const getMenu = () => {
    return (
      <Box
        className={s.Burger__menuWrapper}
        role="presentation"
        onClick={() => toggleDrawer(false)}
        onKeyDown={() => toggleDrawer(false)}
      >
        <List
          key={'burger-menu-list'}
          subheader={
            <ListSubheader
              component="div"
              id="nested-list-subheader"
              className={s.Burger__menuHeader}
            >
              <LogoText />
              <CloseIcon className={s.Burger__menuClose} onClick={() => toggleDrawer(false)} />
            </ListSubheader>
          }
        >
          {navItems
            .filter((item) => filterNavItem(item, authDetails))
            .map((item, i) => getMenuItem(i, item))}
        </List>
      </Box>
    );
  };

  return (
    <>
      <Drawer
        anchor={'right'}
        open={isOpen}
        onClose={() => toggleDrawer(false)}
        slotProps={{ backdrop: { invisible: true } }}
      >
        {getMenu()}
      </Drawer>
      <div
        onClick={() => toggleDrawer(true)}
        className={cn(s.Burger, { isOpen })}
        data-testid="navigation_menu"
      >
        <div className={cn(s.Burger__line, { transparent, dark, isSticky })} />
        <div className={cn(s.Burger__line, { transparent, dark, isSticky })} />
        <div className={cn(s.Burger__line, { transparent, dark, isSticky })} />
      </div>
      <NotificationDialog />
    </>
  );
};

export default observer(Burger);
